import { $color } from "@/utils/cssVarUtils"
import { inputAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"
import { sizesOptions } from "../common/sizeOptions"

const baseStyle: PartsStyleObject<typeof parts> = {
  field: {
    width: "100%",
    minWidth: 0,
    outline: 0,
    position: "relative",
    appearance: "none",
    transitionProperty: "common",
    transitionDuration: "normal",
    _readOnly: {
      boxShadow: "none !important",
      userSelect: "all",
    },
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
    },
    _placeholder: {
      color: $color('text-placeholder'),
    }
  },
}

const size: Record<string, SystemStyleObject> = {
  ...sizesOptions,
}

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  lg: {
    field: size.lg,
    addon: {
      ...size.lg,
      h: 'auto',
    },
  },
  md: {
    field: size.md,
    addon: {
      ...size.md,
      h: 'auto',
    }
  },
  sm: {
    field: size.sm,
    addon: {
      ...size.sm,
      h: 'auto',
    }
  },
  xs: {
    field: size.xs,
    addon: {
      ...size.xs,
      h: 'auto',
    }
  },
}

const variantOutline: PartsStyleFunction<typeof parts> = (props) => {
  return {
    field: {
      border: "none",
      bg: 'transparent',
      boxShadow: `inset 0 0 0 1px ${$color('bg-input-normal')}`,
      outline: 'none',
      _focus: {
        boxShadow: `inset 0 0 0 1px ${$color('scene-primary-active')}`,
      },
      _hover: {
        bg: '',
      },
      _invalid: {
        boxShadow: `inset 0 0 0 1px ${$color('scene-danger-normal')}`,
      },
    },
    addon: {
      border: 'none',
    },
  }
}

const variantFilled: PartsStyleFunction<typeof parts> = (props) => {
  const { field, addon } = variantOutline(props)

  return {
    field: {
      ...field,
      bg: $color('bg-input-normal'),
    },
    addon: {
      ...addon,
    },
  }
}

const variantFlushed: PartsStyleFunction<typeof parts> = (props) => {
  const { size } = props

  return {
    field: {
      borderBottom: "1px solid",
      borderColor: $color('bg-input-normal'),
      borderRadius: 0,
      px: 0,
      bg: "transparent",
      _focus: {
        borderColor: $color('scene-primary-active'),
        boxShadow: 'none',
      },
      _invalid: {
        boxShadow: 'none',
        borderWidth: '2px',
        borderColor: $color('scene-error-normal'),
      },
      '+ .chakra-input__right-addon': {
        pl: sizes[size].addon?.px,
        pr: 0,
      }
    },
    addon: {
      borderBottom: "1px solid",
      borderColor: $color('bg-input-normal'),
      borderRadius: 0,
      bg: "transparent",
      pr: sizes[size].addon?.px,
    },
  }
}

const variantUnstyled: PartsStyleObject<typeof parts> = {
  field: {
    bg: "transparent",
    px: 0,
    height: "auto",
  },
  addon: {
    bg: "transparent",
    px: 0,
    height: "auto",
  },
}

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
  unstyled: variantUnstyled,
}

const defaultProps = {
  size: "md",
  variant: "filled",
}

export const Input = {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants,
  defaultProps,
}